import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StructureIcon } from "@/components/StructureIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sliders, Save, Loader2, Gauge, Tag } from "lucide-react";
import { toast } from "sonner";

const THEMES = [
  { key: "light", label: "Tema Rojo" },
  { key: "dark", label: "Tema Negro" },
];

const PROD_CATS = [
  { key: "auto_tanques", label: "Auto Tanques" },
  { key: "estaciones", label: "Estaciones" },
  { key: "cilindreras", label: "Cilindreras" },
];
const PROD_CLASSES = ["A", "B", "C"];
const CLASS_COLORS = { A: "#1f2a44", B: "#2b9fd6", C: "#b01e3c" };
const OPS = [
  { key: "mayor", label: "Mayor a" },
  { key: "mayor_igual", label: "Mayor o igual a" },
  { key: "menor", label: "Menor a" },
  { key: "menor_igual", label: "Menor o igual a" },
  { key: "entre", label: "Entre" },
  { key: "igual", label: "Igual a" },
];

const numFmt = (n) => (n === "" || n == null ? "?" : Number.isInteger(Number(n)) ? Number(n) : Math.round(Number(n) * 10) / 10);

function ruleLabel(r) {
  if (!r || !r.op) return "";
  const v1 = numFmt(r.v1);
  const v2 = numFmt(r.v2);
  switch (r.op) {
    case "mayor": return `Mayor a ${v1} TON`;
    case "mayor_igual": return `Mayor o igual a ${v1} TON`;
    case "menor": return `Menor a ${v1} TON`;
    case "menor_igual": return `Menor o igual a ${v1} TON`;
    case "igual": return `Igual a ${v1} TON`;
    case "entre": {
      const a = Number(r.v1), b = Number(r.v2);
      if (Number.isNaN(a) || Number.isNaN(b)) return `Entre ${v2} y ${v1} TON`;
      return `Entre ${numFmt(Math.max(a, b))} y ${numFmt(Math.min(a, b))} TON`;
    }
    default: return "";
  }
}

function VersionSistema() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["system-version"], queryFn: async () => (await api.get("/params/system-version")).data });
  const [v, setV] = useState("");
  useEffect(() => { if (data) setV(data.version || ""); }, [data]);
  const save = useMutation({
    mutationFn: async () => (await api.put("/admin/params/system-version", { version: v })).data,
    onSuccess: (d) => { toast.success("Versión guardada"); qc.setQueryData(["system-version"], d); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const fecha = data?.updated_at ? new Date(data.updated_at).toLocaleString("es-MX", { dateStyle: "long", timeStyle: "short" }) : null;
  return (
    <div className="bg-[#131C2E] border border-[#223048] rounded-xl p-6 shadow-lg" data-testid="version-section">
      <div className="flex items-center justify-between mb-1 gap-3">
        <h2 className="font-display text-lg font-bold text-slate-100 flex items-center gap-2"><Tag className="w-5 h-5 text-red-500" /> Versión del Sistema</h2>
        <Button onClick={() => save.mutate()} disabled={save.isPending || !v.trim() || v.trim() === data?.version} className="bg-red-600 hover:bg-red-700 text-white" data-testid="save-version-button">
          {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Guardar</>}
        </Button>
      </div>
      <p className="text-slate-400 text-sm mb-4">Versión de ZAE Ejecutivo que se muestra a todos los usuarios en el menú de su perfil.</p>
      <div className="flex flex-wrap items-end gap-6">
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Versión actual</span>
          <Input value={v} onChange={(e) => setV(e.target.value)} maxLength={30} placeholder="1.0.0" data-testid="version-input"
            className="mt-1 h-9 w-48 bg-slate-950/60 border-slate-700 text-white font-mono text-sm" />
        </label>
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Última actualización</span>
          <p className="mt-2 text-sm text-slate-200" data-testid="version-updated-at">{fecha ? `${fecha}${data.updated_by ? ` · ${data.updated_by}` : ""}` : "Sin registro"}</p>
        </div>
      </div>
    </div>
  );
}

export default function ParametrosSistema() {
  const qc = useQueryClient();
  const [colors, setColors] = useState({});
  const [rules, setRules] = useState({});

  const { data: structures } = useQuery({ queryKey: ["structures"], queryFn: async () => (await api.get("/structures")).data });
  const { data: saved, isLoading } = useQuery({ queryKey: ["structure-colors"], queryFn: async () => (await api.get("/params/structure-colors")).data });
  const { data: savedRules, isLoading: rulesLoading } = useQuery({ queryKey: ["prod-rules"], queryFn: async () => (await api.get("/params/productividad-rules")).data });

  useEffect(() => { if (saved) setColors(saved); }, [saved]);
  useEffect(() => { if (savedRules) setRules(savedRules); }, [savedRules]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/admin/params/structure-colors", { colors })).data,
    onSuccess: () => { toast.success("Parámetros guardados"); qc.invalidateQueries({ queryKey: ["structure-colors"] }); qc.invalidateQueries({ queryKey: ["sel-colors"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const saveRules = useMutation({
    mutationFn: async () => (await api.put("/admin/params/productividad-rules", { rules })).data,
    onSuccess: () => { toast.success("Reglas guardadas"); qc.invalidateQueries({ queryKey: ["prod-rules"] }); qc.invalidateQueries({ queryKey: ["report"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const setColor = (sk, theme, value) => setColors((c) => ({ ...c, [sk]: { ...(c[sk] || {}), [theme]: value } }));
  const setRule = (ck, cls, field, value) =>
    setRules((r) => ({
      ...r,
      [ck]: { ...(r[ck] || {}), [cls]: { ...((r[ck] || {})[cls] || {}), [field]: field.startsWith("v") ? (value === "" ? "" : Number(value)) : value } },
    }));
  const valid = (v) => /^#([0-9a-fA-F]{6})$/.test(v || "");

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-widest text-red-500 font-semibold">Administración</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight flex items-center gap-2">
          <Sliders className="w-8 h-8 text-red-500" /> Parámetros del Sistema
        </h1>
        <p className="text-slate-400 text-sm mt-1">Configuraciones generales de apariencia y comportamiento</p>
      </div>

      <VersionSistema />

      <div className="bg-[#131C2E] border border-[#223048] rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display text-lg font-bold text-slate-100">Configuración visual de Estructura Organizacional</h2>
          <Button onClick={() => save.mutate()} disabled={save.isPending} className="bg-red-600 hover:bg-red-700 text-white" data-testid="save-params-button">
            {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Guardar</>}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mb-5">Color del contenedor del ítem seleccionado, por estructura y por tema.</p>

        {isLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>
        ) : (
          <div className="space-y-3">
            {structures?.map((s) => (
              <div key={s.key} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center bg-slate-900/40 border border-[#223048] rounded-lg p-3" data-testid={`param-row-${s.key}`}>
                <div className="flex items-center gap-2 text-white font-semibold">
                  <StructureIcon name={s.icon} className="w-4 h-4" style={{ color: s.color }} /> {s.name}
                </div>
                {THEMES.map((t) => {
                  const val = colors[s.key]?.[t.key] || s.color;
                  return (
                    <div key={t.key}>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t.label}</p>
                      <div className="flex items-center gap-2">
                        <input type="color" value={valid(val) ? val : s.color} onChange={(e) => setColor(s.key, t.key, e.target.value)}
                          data-testid={`color-${s.key}-${t.key}`} className="w-10 h-9 rounded bg-transparent border border-[#223048] cursor-pointer" />
                        <Input value={val} onChange={(e) => setColor(s.key, t.key, e.target.value)} placeholder="#8F2430"
                          data-testid={`hex-${s.key}-${t.key}`} className="h-9 bg-slate-950/60 border-slate-700 text-white font-mono text-sm" />
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-[#131C2E] border border-[#223048] rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display text-lg font-bold text-slate-100 flex items-center gap-2"><Gauge className="w-5 h-5 text-red-500" /> Reglas de Productividades</h2>
          <Button onClick={() => saveRules.mutate()} disabled={saveRules.isPending} className="bg-red-600 hover:bg-red-700 text-white" data-testid="save-prod-rules-button">
            {saveRules.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Guardar</>}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mb-5">Define para cada clase ("A", "B", "C") el operador y los valores en toneladas. "Entre" usa dos valores.</p>

        {rulesLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            {PROD_CATS.map((cat) => (
              <div key={cat.key} className="bg-slate-900/40 border border-[#223048] rounded-lg p-4" data-testid={`prod-rule-${cat.key}`}>
                <p className="text-white font-semibold mb-3">{cat.label}</p>
                <div className="space-y-2">
                  {PROD_CLASSES.map((cls) => {
                    const rc = (rules[cat.key] || {})[cls] || {};
                    return (
                      <div key={cls} className="flex flex-wrap items-center gap-2">
                        <span className="px-2.5 py-1.5 text-white text-xs font-bold rounded w-9 text-center" style={{ backgroundColor: CLASS_COLORS[cls] }}>"{cls}"</span>
                        <select value={rc.op || "mayor"} onChange={(e) => setRule(cat.key, cls, "op", e.target.value)}
                          data-testid={`prod-op-${cat.key}-${cls}`} className="h-9 rounded bg-slate-950/60 border border-slate-700 text-white text-sm px-2 min-w-[140px]">
                          {OPS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                        </select>
                        <Input type="number" step="0.1" value={rc.v1 ?? ""} onChange={(e) => setRule(cat.key, cls, "v1", e.target.value)}
                          data-testid={`prod-v1-${cat.key}-${cls}`} placeholder="TON" className="h-9 w-24 bg-slate-950/60 border-slate-700 text-white font-mono text-sm" />
                        {rc.op === "entre" && (
                          <>
                            <span className="text-slate-500 text-xs">y</span>
                            <Input type="number" step="0.1" value={rc.v2 ?? ""} onChange={(e) => setRule(cat.key, cls, "v2", e.target.value)}
                              data-testid={`prod-v2-${cat.key}-${cls}`} placeholder="TON" className="h-9 w-24 bg-slate-950/60 border-slate-700 text-white font-mono text-sm" />
                          </>
                        )}
                        <span className="text-[11px] text-slate-400 ml-1">{ruleLabel(rc)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
